/**
 * 合成着色器（M3-09 / DESIGN.md §4.4 第 8 步 + composite() 公式）。
 *
 * 这是 Render Graph 里 `Blend` 节点的真身：把一个图层（`u_source`，已经过
 * fillOpacity/蒙版/剪贴调制的 alpha）以 `u_mode` 混合模式、`u_opacity` 不透明度
 * 混进背景（`u_backdrop`）。混合函数与色彩空间转换复用 blend/generate.ts 的
 * 公式（十六种模式只有一份实现，见该文件头注释），这里只加 Porter-Duff
 * 合成与纹理采样这一层。
 *
 * fillOpacity/蒙版/剪贴调制没有单独的着色器：它们都是「alpha 再乘一个系数」，
 * 直接在这里用 `u_maskEnabled`/`u_clipEnabled` 两个开关叠加进 source alpha，
 * 省掉三个额外的 draw（执行器里 `drawsPerTile` 的成本函数因此按«节点」计而非
 * 按«draw」计，见 gpu/executor 的说明）。
 *
 * 蒙版/剪贴都读纹理的 **alpha 通道**（不是 R 通道）——与应用层的约定一致
 * （`app/commands/mask.ts` 把蒙版值写进 alpha，`composite.ts` 的 `maskValueAt`
 * 也读 alpha；剪贴基底同理，取的是它被 blend 前那份像素的 alpha）。
 *
 * 行序（GLSL）：本着色器可能同时采样«上传纹理»（source/mask，来自 TileStore
 * 的原始像素）与«渲染纹理»（backdrop/clip，前一个 Blend 的输出）。两者混采
 * 在同一个 shader 里天然行序不一致——除非像这里一样，**全部**纹理都用
 * `texelFetch(tex, ivec2(gl_FragCoord.xy), 0)` 直采，不做任何手工翻转。
 * 这样每次渲染都保持“翻转状态”不变地往下传，直到管线最末端一次 `readback()`
 * 抵消最初 `writeTexture` 的预翻转——与 adjust 着色器的道理完全一致（那里
 * 的注释已经证明这条链路是对的），只是这里链条更长。
 */
import type { ShaderSource } from '../../backend';
import { GLSL_DIALECT, WGSL_DIALECT } from '../blend/dialect';
import { colorSpaceFunctions, blendFunctions, dispatchFunction, vecCond } from '../blend/generate';
import { nonSeparableHelpers } from '../blend/nonseparable';

function wgslSource(): string {
  const d = WGSL_DIALECT;
  return `
struct Params {
  u_clipEnabled: f32,
  u_linear: f32,
  u_maskEnabled: f32,
  u_mode: f32,
  u_opacity: f32,
};
@group(0) @binding(0) var<uniform> params: Params;
@group(0) @binding(1) var u_backdrop: texture_2d<f32>;
@group(0) @binding(2) var u_clip: texture_2d<f32>;
@group(0) @binding(3) var u_mask: texture_2d<f32>;
@group(0) @binding(4) var u_source: texture_2d<f32>;

@vertex
fn vs_main(@builtin(vertex_index) idx: u32) -> @builtin(position) vec4f {
  var pos = array<vec2f, 3>(vec2f(-1.0, -1.0), vec2f(3.0, -1.0), vec2f(-1.0, 3.0));
  return vec4f(pos[idx], 0.0, 1.0);
}

${colorSpaceFunctions(d)}

${nonSeparableHelpers(d)}

${blendFunctions(d)}

${dispatchFunction(d)}

@fragment
fn fs_main(@builtin(position) pos: vec4f) -> @location(0) vec4f {
  let xy = vec2<i32>(pos.xy);
  let cb = textureLoad(u_backdrop, xy, 0);
  var cs = textureLoad(u_source, xy, 0);
  cs.a = cs.a * params.u_opacity;
  if (params.u_maskEnabled > 0.5) {
    cs.a = cs.a * textureLoad(u_mask, xy, 0).a;
  }
  if (params.u_clipEnabled > 0.5) {
    cs.a = cs.a * textureLoad(u_clip, xy, 0).a;
  }

  let inLinear = params.u_linear > 0.5;
  let cb_work = ${d.select('cb.rgb', 'srgb_to_linear(cb.rgb)', vecCond(d, 'inLinear'))};
  let cs_work = ${d.select('cs.rgb', 'srgb_to_linear(cs.rgb)', vecCond(d, 'inLinear'))};
  let blended = blend_dispatch(params.u_mode, cb_work, cs_work);

  // Porter-Duff source-over，混合结果只影响 cb∩cs 的区域（DESIGN §4.4 的 composite()）
  let a = cs.a + cb.a * (1.0 - cs.a);
  var rgb_work = (1.0 - cb.a) * cs.a * cs_work
               + cb.a * cs.a * blended
               + (1.0 - cs.a) * cb.a * cb_work;
  if (a > 0.0) { rgb_work = rgb_work / a; } else { rgb_work = vec3f(0.0); }
  let rgb = ${d.select('rgb_work', 'linear_to_srgb(rgb_work)', vecCond(d, 'inLinear'))};
  return vec4f(rgb, a);
}
`;
}

function glslFragmentSource(): string {
  const d = GLSL_DIALECT;
  return `#version 300 es
precision highp float;
uniform float u_clipEnabled;
uniform float u_linear;
uniform float u_maskEnabled;
uniform float u_mode;
uniform float u_opacity;
uniform sampler2D u_backdrop;
uniform sampler2D u_clip;
uniform sampler2D u_mask;
uniform sampler2D u_source;
out vec4 fragColor;

${colorSpaceFunctions(d)}

${nonSeparableHelpers(d)}

${blendFunctions(d)}

${dispatchFunction(d)}

void main() {
  // 全部纹理直采 fragCoord，理由见本文件头注释的行序说明
  ivec2 xy = ivec2(gl_FragCoord.xy);
  vec4 cb = texelFetch(u_backdrop, xy, 0);
  vec4 cs = texelFetch(u_source, xy, 0);
  cs.a *= u_opacity;
  if (u_maskEnabled > 0.5) {
    cs.a *= texelFetch(u_mask, xy, 0).a;
  }
  if (u_clipEnabled > 0.5) {
    cs.a *= texelFetch(u_clip, xy, 0).a;
  }

  bool inLinear = u_linear > 0.5;
  vec3 cb_work = ${d.select('cb.rgb', 'srgb_to_linear(cb.rgb)', vecCond(d, 'inLinear'))};
  vec3 cs_work = ${d.select('cs.rgb', 'srgb_to_linear(cs.rgb)', vecCond(d, 'inLinear'))};
  vec3 blended = blend_dispatch(u_mode, cb_work, cs_work);

  float a = cs.a + cb.a * (1.0 - cs.a);
  vec3 rgb_work = (1.0 - cb.a) * cs.a * cs_work
                + cb.a * cs.a * blended
                + (1.0 - cs.a) * cb.a * cb_work;
  rgb_work = a > 0.0 ? rgb_work / a : vec3(0.0);
  vec3 rgb = ${d.select('rgb_work', 'linear_to_srgb(rgb_work)', vecCond(d, 'inLinear'))};
  fragColor = vec4(rgb, a);
}
`;
}

let cached: ShaderSource | null = null;

export function compositeShaderSource(): ShaderSource {
  cached ??= {
    wgsl: wgslSource(),
    glsl: { vertex: GLSL_DIALECT.fullscreenPreamble(), fragment: glslFragmentSource() },
  };
  return cached;
}

/** 供测试用：绕过缓存重新生成。 */
export function regenerateCompositeShaderSource(): ShaderSource {
  cached = null;
  return compositeShaderSource();
}
